import { CircleCheck, TriangleAlert } from 'lucide-react';
import type { ChannexQuarantinedRevision } from '@/shared/api';
import { Button } from '@/shared/components';
import { replayOutcomeText, replayRefusalText } from './revisionHoldPresentation';
import type { ReplayRowState } from './useRevisionReplay';

interface Props {
  row: ChannexQuarantinedRevision;
  state: ReplayRowState | undefined;
  onReplay: () => void;
}

/**
 * Die eine nächste Aktion einer Revision in der Quarantäne, direkt unter ihrem Grund. Ob es sie
 * gibt, entscheidet der Server (`canReplay`); ohne sie steht der Grund da, warum nicht. Ein
 * Ergebnis erscheint erst mit der Antwort des Servers.
 */
export function RevisionHoldReplayAction({ row, state, onReplay }: Props) {
  if (state?.kind === 'done' && state.result.outcome !== 'StillQuarantined') {
    const applied = state.result.outcome === 'Applied';
    return (
      <p
        role="status"
        className={`mt-2 flex items-start gap-1.5 text-sm ${applied ? 'text-sage' : 'text-ink-soft'}`}
      >
        <CircleCheck size={16} className="mt-0.5 shrink-0" aria-hidden />
        {replayOutcomeText(state.result, row.id)}
      </p>
    );
  }
  if (!row.canReplay) {
    return <p className="text-ink-soft mt-2 text-[13px]">{replayRefusalText(row.replayRefusal)}</p>;
  }
  const running = state?.kind === 'running';
  return (
    <div className="mt-2.5">
      {state?.kind === 'done' && (
        <p role="status" className="text-ink mb-2 flex items-start gap-1.5 text-sm">
          <TriangleAlert size={16} className="text-amber mt-0.5 shrink-0" aria-hidden />
          {replayOutcomeText(state.result, row.id)}
        </p>
      )}
      <Button
        type="button"
        variant="brand"
        className="min-h-11"
        loading={running}
        disabled={running}
        onClick={onReplay}
      >
        {running ? 'Wird verarbeitet …' : 'Erneut verarbeiten'}
      </Button>
      {state?.kind === 'failed' && (
        <p role="alert" className="text-sealwax mt-1.5 text-[13px]">
          {state.message}
        </p>
      )}
    </div>
  );
}
